/**
 * useViewDrafts — the view CONFIGURATOR's state, one config PER TAB.
 *
 * The configurator reconfigures the current view in real time (reason about the
 * system / reshape it) and, when you want, saves. Its state is a per-tab
 * `ViewConfig` held in the workspace store (`get/setBuilderConfig`), persisted
 * per tab across reloads — a config in one tab never leaks to another.
 *
 *  - PROJECTION tier: the config's structured clause rows compile live via the
 *    engine's `parseViewSource` and project onto the board through
 *    `ActiveViewContext.draftView` — but ONLY while the config is `dirty` (the
 *    user has diverged from the base), so a freshly-opened view keeps its
 *    WYSIWYG write-back rail. Switching tabs swaps the projection automatically.
 *  - SAVE tier: writes to `.arch` text via the mutation service — either
 *    "Update view", MODIFYING an existing view in place (`mutateViewBatch` with
 *    `setKnobs` + `setClauses` — the header and clause regions are written by
 *    their own op, in one atomic write; name locked), or "Save as view",
 *    CREATING a new one (`scaffoldView`, name editable, blocked on a name
 *    collision).
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { parseViewSource, type ViewDeclV2, type ViewMutation } from "@archlang/engine";
import { useActiveView } from "../workbench/ActiveViewContext.js";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useWorkspace } from "../workbench/WorkspaceContext.js";
import { useActiveFile, useActiveTabId } from "../workbench/WorkspaceStore.js";
import { useModelMutations, type MutationResult } from "../workbench/mutations.js";
import { rowText, seedShowAll, normalizeShowAll, isKnobRowText as isKnobRow, type ClauseRow, type ViewConfig } from "./clauseModel.js";
import { viewDeclToDraft, sliceSourceFrom } from "./viewDeclToDraft.js";
import { findView, qualifiedViewName } from "./viewRepr.js";

export interface ViewDraft {
  readonly name: string;
  readonly rows: readonly ClauseRow[];
}

/** Render the draft's COMPLETE rows to clause source lines (incomplete rows —
 *  an atom-less show, an empty group key — are skipped, flagged in the UI). */
export function draftClauseTexts(draft: ViewDraft): string[] {
  return draft.rows.map(rowText).filter((t): t is string => t !== null);
}

/** The placeholder view name a still-unnamed config compiles under, so it can be
 *  projected before it's named. Naming is a SAVE requirement (identity +
 *  collision), never a projection one. */
export const UNNAMED_DRAFT_NAME = "Untitled";

/** Compile a draft into a ViewDeclV2 + user-facing errors. A blank name is NOT
 *  an error here — it compiles under {@link UNNAMED_DRAFT_NAME} so the projection
 *  works; save-time gating lives in `saveToModel`. */
export function compileDraft(draft: ViewDraft): { view: ViewDeclV2 | null; errors: readonly string[] } {
  const name = draft.name.trim() || UNNAMED_DRAFT_NAME;
  const body = draftClauseTexts(draft);
  const src = `view ${name} {\n${body.map((c) => `    ${c}`).join("\n")}\n}`;
  const r = parseViewSource(src, "__draft__.arch");
  if (r.errors.length > 0) {
    return { view: null, errors: r.errors.map((e) => e.message) };
  }
  return { view: r.decl, errors: [] };
}

export interface ViewDraftsApi {
  /** The config being edited (never null — an empty `show *` scratch by default). */
  readonly editing: ViewDraft;
  /** Live compile of `editing`: parse errors (empty = valid). */
  readonly errors: readonly string[];
  /** True while the config is projecting onto the board (it has diverged). */
  readonly applied: boolean;
  /** A DIFFERENT model view already carries this name (a new-view save collides). */
  readonly nameCollision: boolean;
  /** True when the config is the untouched scratch — safe to auto-prepopulate
   *  from the active view without clobbering real work. */
  readonly pristine: boolean;
  /** The config is MODIFYING an existing model view — its name is locked and Save
   *  updates it in place (vs. creating a new view). */
  readonly isModifying: boolean;

  setName(name: string): void;
  setRow(index: number, row: ClauseRow): void;
  addRow(row: ClauseRow): void;
  removeRow(index: number): void;
  moveRow(index: number, dir: -1 | 1): void;
  /** Replace the whole row list atomically — the section composer's primitive
   *  for reorder / set-single-by-kind / swap-representation in one update. */
  replaceRows(rows: readonly ClauseRow[]): void;
  /** Prepopulate from a model view → MODIFY it (name locked, not yet projected). */
  seedFromView(view: ViewDeclV2): void;
  /** Drop the live reconfiguration: when modifying, REVERT to the saved view's
   *  clauses (stay on it, un-dirtied); otherwise clear the config. Either way the
   *  board returns to its actual state. */
  reset(): void;
  /** Persist to `.arch` text: modify the base view in place, or create a new one. */
  saveToModel(): MutationResult;
}

const FRESH_ROWS: readonly ClauseRow[] = [seedShowAll()];
const FRESH_DRAFT: ViewDraft = { name: "", rows: FRESH_ROWS };
/** The config a first edit forks from when a tab has none yet. */
const SCRATCH: ViewConfig = { rows: FRESH_ROWS, baseViewName: null, name: "", dirty: false };

export function useViewDrafts(): ViewDraftsApi {
  const store = useWorkspace();
  const activeFile = useActiveFile(store);
  const shared = useWorkspaceModel();
  const mutations = useModelMutations();
  const { setDraftView, setActiveViewName } = useActiveView();

  const activeTabId = useActiveTabId(store);
  // The config lives in the store, keyed by the active tab — read reactively.
  // Snapshots return the STORED object (stable identity), so unrelated store
  // notifications (pan / selection) don't churn this hook or the projection.
  const config = useSyncExternalStore(store.subscribe, () =>
    store.getBuilderConfig(store.getState().activeTabId),
  );

  // `name` stays the BARE display/compile name; `baseViewName` is the
  // QUALIFIED modify target (owner-scoped views make bare names ambiguous —
  // a bare target mutated the wrong same-named view, r34), and a qualified
  // name is not a legal draft-view header.
  const editing: ViewDraft = useMemo(
    () => (config ? { name: config.name || config.baseViewName || "", rows: config.rows } : FRESH_DRAFT),
    [config],
  );

  const { view: compiled, errors } = useMemo(() => compileDraft(editing), [editing]);

  const isModifying = config?.baseViewName != null;
  const applied = !!config?.dirty;
  const nameCollision = useMemo(
    () =>
      !isModifying &&
      editing.name.trim() !== "" &&
      // A NEW view scaffolds at top level, so only a TOP-LEVEL view of that
      // name collides — a scoped `in X view <name>` is a different identity
      // (per-scope uniqueness) and must not block the create (r34).
      (shared.resolvedModel?.views ?? []).some(
        (v) => qualifiedViewName(v) === editing.name.trim(),
      ),
    [isModifying, shared.resolvedModel, editing.name],
  );
  // A tab with no config (or a fresh scratch) is safe to auto-prepopulate on open.
  const pristine = !config || (!config.dirty && config.baseViewName == null);

  // Live projection: mirror the compile onto the board WHILE dirty; null
  // otherwise (a pristine edit-existing keeps the view's WYSIWYG write-back rail,
  // `viewPlan` gates it on `!draftView`). The `config`-keyed deps also swap the
  // projection on tab switch (config changes with the active tab).
  useEffect(() => {
    // Session key = the builder tab — renames within one session (the create
    // flow recompiles under a new name per name keystroke) keep the shared
    // ::draft knob/reveal state; a tab switch to another draft drops it. The
    // key is passed WHILE the session lives (`applied`) even when the compile
    // transiently fails (an unparseable name intermediate projects null but
    // does not end the session); `applied` turning false (reset/save) ends it
    // — key null, boundary crossed, state dropped. `activeTabId` is a REAL
    // dep (not a getState() read): a duplicated tab SHARES its source's
    // builderConfig reference, so `config`/`compiled` keep their identity
    // across the switch — without the id dep the effect never refired and the
    // clone inherited the source session's ::draft state.
    setDraftView(applied && compiled ? compiled : null, applied ? activeTabId : null);
  }, [applied, compiled, setDraftView, activeTabId]);

  const write = useCallback(
    (patch: Partial<ViewConfig>): void => {
      const id = store.getState().activeTabId;
      const cur = store.getBuilderConfig(id) ?? SCRATCH;
      store.setBuilderConfig(id, { ...cur, ...patch });
    },
    [store],
  );

  // Any row edit: normalize `show *` (special default), mark dirty (project from
  // here). Funnels every structured mutation so the rule holds for the
  // projection AND the save.
  const editRows = useCallback(
    (rows: readonly ClauseRow[]): void => write({ rows: normalizeShowAll([...rows]), dirty: true }),
    [write],
  );

  // Load a model view into this tab's config → MODIFY it: rows from the view,
  // name locked to it, un-dirtied (not projected until a real edit diverges it).
  // Shared by open-on-a-view (seedFromView) and reset-while-modifying (revert).
  const seedFrom = useCallback(
    (view: ViewDeclV2): void => {
      const draft = viewDeclToDraft(view, sliceSourceFrom(shared.compiledSources));
      // Snapshot the knob rows at seed time — the modify-save's baseline for
      // "did the USER edit a knob row" (see ViewConfig.seedKnobs).
      const seedKnobs = draft.rows
        .filter((r): r is Extract<ClauseRow, { kind: "raw" }> => r.kind === "raw" && isKnobRow(r.text))
        .map((r) => r.text.trim());
      // QUALIFIED target — resolvers are qualified-first everywhere (findView,
      // engine viewFor), and owner-scoped views make same-bare-name views
      // legal: a bare target here rewrote the wrong view on save (r34).
      write({ rows: draft.rows, baseViewName: qualifiedViewName(view), name: view.name, dirty: false, seedKnobs });
    },
    [write, shared.compiledSources],
  );

  // Reconcile a MODIFYING config whose base view vanished — deleted or renamed
  // elsewhere (a text edit, another session, a persisted config for a view that's
  // since gone). Demote it to a scratch config: keep the rows (don't lose the
  // in-flight reconfiguration), UNLOCK the name, keep projecting — instead of
  // leaving the name locked to a phantom + Save failing `unknown-target`. The old
  // name rides along as the proposed create name. Compile-driven (`resolvedModel`
  // IS the recompile); guarded on a transient null model so a mid-recompile blip
  // never demotes. After the write `isModifying` is false, so it can't re-fire.
  useEffect(() => {
    if (!isModifying || !shared.resolvedModel) return;
    const base = config!.baseViewName!;
    // Qualified-first lookup (bare fallback covers legacy persisted configs).
    if (findView(shared.resolvedModel.views, base)) return;
    write({ baseViewName: null, dirty: true });
  }, [isModifying, config, shared.resolvedModel, write]);

  return useMemo<ViewDraftsApi>(
    () => ({
      editing,
      errors,
      applied,
      nameCollision,
      pristine,
      isModifying,

      setName(name) {
        if (isModifying) return; // name is locked while modifying an existing view
        write({ name, dirty: true });
      },
      setRow(index, row) {
        editRows(editing.rows.map((r, i) => (i === index ? row : r)));
      },
      addRow(row) {
        editRows([...editing.rows, row]);
      },
      removeRow(index) {
        editRows(editing.rows.filter((_, i) => i !== index));
      },
      moveRow(index, dir) {
        const j = index + dir;
        if (j < 0 || j >= editing.rows.length) return;
        const rows = [...editing.rows];
        [rows[index], rows[j]] = [rows[j]!, rows[index]!];
        editRows(rows);
      },
      replaceRows(rows) {
        editRows([...rows]);
      },

      seedFromView(view) {
        seedFrom(view);
      },

      reset() {
        // Modifying a view → revert to its SAVED clauses (re-seed, un-dirtied) so
        // the board snaps back to the real view but you stay on it. Creating →
        // clear the config; the board returns to its actual (unconfigured) view.
        const id = store.getState().activeTabId;
        const base = store.getBuilderConfig(id)?.baseViewName;
        const view = base ? findView(shared.resolvedModel?.views, base) : undefined;
        if (view) {
          seedFrom(view);
          return;
        }
        store.setBuilderConfig(id, null);
        setDraftView(null);
      },

      saveToModel() {
        const name = (config?.name || config?.baseViewName || "").trim();
        if (!name) {
          return { status: "refused", reason: "name-required", message: "name the view before saving" };
        }
        if (!compiled || errors.length > 0) {
          return { status: "refused", reason: "draft-invalid", message: errors.join("; ") || "draft is invalid" };
        }
        if (nameCollision) {
          return {
            status: "refused",
            reason: "name-collision",
            message: `a view named '${name}' already exists in the model`,
          };
        }
        const clauses = draftClauseTexts(editing);

        let r: MutationResult;
        if (isModifying) {
          // Update the existing view IN PLACE. The two regions inside the braces
          // are written by their own ops — `setKnobs` for the header knob decls,
          // `setClauses` for everything after them — batched into ONE write, so
          // the second is not synthesized against spans the first already moved.
          // Only the knob region is touched when the knob rows actually changed;
          // an untouched header keeps its exact bytes (comments, formatting).
          const draftKnobs = clauses.filter(isKnobRow).map((t) => t.trim());
          // Baseline = the knob rows AS SEEDED into this config — a concurrent
          // .arch retune of a knob decl must not read as a user knob edit.
          // Legacy configs without the snapshot fall back to a live
          // re-derivation of the base view.
          const base = findView(shared.resolvedModel?.views, config!.baseViewName!);
          const baseKnobs =
            config!.seedKnobs ??
            (base
              ? viewDeclToDraft(base, sliceSourceFrom(shared.compiledSources))
                  .rows.filter((row) => row.kind === "raw" && isKnobRow(row.text))
                  .map((row) => (row as { text: string }).text.trim())
              : []);
          const knobsChanged =
            draftKnobs.length !== baseKnobs.length || draftKnobs.some((t, i) => t !== baseKnobs[i]);
          const clausesOnly = clauses.filter((t) => !isKnobRow(t));
          const ops: ViewMutation[] = [
            ...(knobsChanged ? [{ op: "setKnobs" as const, knobTexts: draftKnobs }] : []),
            { op: "setClauses" as const, clauseTexts: clausesOnly },
          ];
          r = mutations.mutateViewBatch(config!.baseViewName!, ops);
        } else {
          // §3.4 target-file rule (same as root module adds): active .arch file,
          // else the first .arch in the compiled workspace.
          const abs =
            activeFile && activeFile.endsWith(".arch")
              ? activeFile.startsWith("/")
                ? activeFile
                : "/" + activeFile
              : [...(shared.compiledSources?.keys() ?? [])].find((k) => k.endsWith(".arch"));
          if (!abs) return { status: "unknown-target" };
          r = mutations.scaffoldView({ absFilePath: abs, name, clauses });
        }
        if (r.status === "applied") {
          // Saved — the view now lives in the model; drop the config + projection
          // so the board shows the real (saved) view. A NEW view becomes the pick.
          store.setBuilderConfig(store.getState().activeTabId, null);
          setDraftView(null);
          if (!isModifying) setActiveViewName(name);
        }
        return r;
      },
    }),
    [
      editing,
      errors,
      applied,
      nameCollision,
      pristine,
      isModifying,
      config,
      compiled,
      write,
      editRows,
      seedFrom,
      store,
      mutations,
      setDraftView,
      setActiveViewName,
      activeFile,
      shared.compiledSources,
      shared.resolvedModel,
    ],
  );
}
